import React, { useEffect } from "react";

const PolygonDrawer = ({
  value,
  setLinePoints,
  setStartLinePoints,
  setEndLinePoints,
  setAreaPoints, 
  children,
  closePolygonThreshold,
  selectedRowIndex,
  rowsData,
  isShowAll,
  areaPoints,
  linePoints,
  startLinePoints,
  endLinePoints,
  ...restProps
}) => {
  const [polygonPoints, setPolygonPoints] = React.useState([]);
  const [isHoveringClosing, setIsHoveringClosing] = React.useState(true);
  const [hoverPoint, setHoverPoint] = React.useState([0.5, 0.5]);
  const polygonDrawerRef = React.useRef(null);
  const val = (value || []);

  const getPos = (evt) => {
    const { pageX, pageY } = evt;
    const bounds = polygonDrawerRef.current.getBoundingClientRect();
    const { width, height, top, left } = bounds;
    const x = (pageX - left) / width;
    const y = (pageY - top) / height;
    return [x, y];
  }

  const isPointNearClosing = (point) => {
    const [x, y] = point;
    const closingPoint = polygonPoints[0];
    if (closingPoint) {
      const hDist = closingPoint[0] - x;
      const vDist = closingPoint[1] - y;
      const dist = Math.sqrt(hDist ** 2 + vDist ** 2) * 100;
      const maxDist = isNaN(+closePolygonThreshold) ? 3 : +closePolygonThreshold
      return dist <= maxDist;
    }
    return false;
  }

  const handleClick = (evt) => {
    if (rowsData[selectedRowIndex]?.is_full_or_region_selected) {
      if (value && value.length < 1) {
        if (rowsData[selectedRowIndex]?.is_polygon_region_selected) {
          if ((selectedRowIndex && selectedRowIndex > 0) || selectedRowIndex === 0) {
            if (rowsData[selectedRowIndex].is_selected) {
              const point = getPos(evt);
              if (isPointNearClosing(point)) {
                setAreaPoints([...val, polygonPoints]);
                setPolygonPoints([]);
              }
              else {
                setPolygonPoints([...polygonPoints, point]);
              }
            }
          }
        }
      }
    }
    else if (rowsData[selectedRowIndex]?.is_draw_line_selected) {
      if (value && value.length < rowsData[selectedRowIndex]?.line_count) {
        if ((selectedRowIndex && selectedRowIndex > 0) || selectedRowIndex === 0) {
          if (rowsData[selectedRowIndex].is_selected) {
            const point = getPos(evt);
            if (rowsData[selectedRowIndex]?.is_draw_line_selected && polygonPoints.length === 1) {
              setLinePoints([...val, [...polygonPoints, point]]);
              setPolygonPoints([]);
            }
            else {
              setPolygonPoints([...polygonPoints, point]);
            }
          }
        }
      }
    }
    else if (rowsData[selectedRowIndex]?.is_start_line_selected) {
      if (value && value.length < 1) {
        if ((selectedRowIndex && selectedRowIndex > 0) || selectedRowIndex === 0) {
          if (rowsData[selectedRowIndex].is_selected) {
            const point = getPos(evt);
            if (rowsData[selectedRowIndex]?.is_start_line_selected && polygonPoints.length === 1) {
              setStartLinePoints([...val, [...polygonPoints, point]]);
              setPolygonPoints([]);
            }
            else {
              setPolygonPoints([...polygonPoints, point]);
            }
          }
        }
      }
    }
    else if (rowsData[selectedRowIndex]?.is_end_line_selected) {
      if (value && value.length < 1) {
        if ((selectedRowIndex && selectedRowIndex > 0) || selectedRowIndex === 0) {
          if (rowsData[selectedRowIndex].is_selected) {
            const point = getPos(evt);
            if (rowsData[selectedRowIndex]?.is_end_line_selected && polygonPoints.length === 1) {
              setEndLinePoints([...val, [...polygonPoints, point]]);
              setPolygonPoints([]);
            }
            else {
              setPolygonPoints([...polygonPoints, point]);
            }
          }
        }
      }
    }
  }

  const handleHover = (evt) => {
    const point = getPos(evt);
    setHoverPoint(point);
    setIsHoveringClosing(isPointNearClosing(point));
  }

  const getStyle = (point) => {
    const left = (point[0] * 100).toFixed(6);
    const top = (point[1] * 100).toFixed(6);
    return { left: `${left}%`, top: `${top}%` };
  }

  const getPathStr = (point) => {
    const left = (point[0] * 100).toFixed(6);
    const top = (point[1] * 100).toFixed(6);
    return left + ' ' + top;
  }

  // Determine what to render based on showAll flag
  const shouldRenderAllElements = isShowAll && (
    (Array.isArray(areaPoints) && areaPoints.length > 0) ||
    (Array.isArray(linePoints) && linePoints.length > 0) ||
    (Array.isArray(startLinePoints) && startLinePoints.length > 0) ||
    (Array.isArray(endLinePoints) && endLinePoints.length > 0)
  );

  // Safe array accessors
  const safeAreaPoints = Array.isArray(areaPoints) ? areaPoints : [];
  const safeLinePoints = Array.isArray(linePoints) ? linePoints : [];
  const safeStartLinePoints = Array.isArray(startLinePoints) ? startLinePoints : [];
  const safeEndLinePoints = Array.isArray(endLinePoints) ? endLinePoints : [];

  useEffect(() => {
    if (value.length === 0) {
      setPolygonPoints([])
    }
    if (!val) {
      setPolygonPoints([])
    }
  }, [value]);

  return (
    <div
      className="polygon-drawer"
      {...restProps}
    >
      <div className="polygon-drawer-content">
        {children}
      </div>
      <div style={getStyle(hoverPoint)} className="polygon-point polygon-drawer-hover" />

      <svg xmlns="http://www.w3.org/2000/svg" className="polygon-container" width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
        {/* Render area polygons - both normal mode and show all mode */}
        {(shouldRenderAllElements ? areaPoints : (Array.isArray(val) ? val : []))?.map((polygon, polygonIndex) => {
          // Skip if polygon is not an array (e.g., if it's true or other values)
          if (!Array.isArray(polygon)) return null;
          return (
            <path
              key={`area-${polygonIndex}`}
              className="svg-polygon-path"
              d={polygon.map((p, i) => `${'LM'[+!i]}${getPathStr(p)}`).join(' ') + 'Z'}
              fill="rgba(100, 150, 255, 0.3)"
              stroke="blue"
              strokeWidth="2"
            />
          );
        })}

        {/* When showing all, render line points as separate line elements */}
        {shouldRenderAllElements && safeLinePoints.length > 0 && (() => {
          // linePoints is [point1, point2] where each point is [x, y]
          if (safeLinePoints.length >= 2 && Array.isArray(safeLinePoints[0]) && Array.isArray(safeLinePoints[1])) {
            const x1 = (safeLinePoints[0][0] || 0) * 100;
            const y1 = (safeLinePoints[0][1] || 0) * 100;
            const x2 = (safeLinePoints[1][0] || 0) * 100;
            const y2 = (safeLinePoints[1][1] || 0) * 100;
            
            if (!isNaN(x1) && !isNaN(y1) && !isNaN(x2) && !isNaN(y2)) {
              return <line key="line-0" x1={x1} y1={y1} x2={x2} y2={y2} stroke="orange" strokeWidth="0.5" />;
            }
          }
          return null;
        })()}

        {/* When showing all, render start line points */}
        {shouldRenderAllElements && safeStartLinePoints.length > 0 && (() => {
          if (safeStartLinePoints.length >= 2 && Array.isArray(safeStartLinePoints[0]) && Array.isArray(safeStartLinePoints[1])) {
            const x1 = (safeStartLinePoints[0][0] || 0) * 100;
            const y1 = (safeStartLinePoints[0][1] || 0) * 100;
            const x2 = (safeStartLinePoints[1][0] || 0) * 100;
            const y2 = (safeStartLinePoints[1][1] || 0) * 100;
            
            if (!isNaN(x1) && !isNaN(y1) && !isNaN(x2) && !isNaN(y2)) {
              return <line key="start-line-0" x1={x1} y1={y1} x2={x2} y2={y2} stroke="green" strokeWidth="0.5" />;
            }
          }
          return null;
        })()}

        {/* When showing all, render end line points */}
        {shouldRenderAllElements && safeEndLinePoints.length > 0 && (() => {
          if (safeEndLinePoints.length >= 2 && Array.isArray(safeEndLinePoints[0]) && Array.isArray(safeEndLinePoints[1])) {
            const x1 = (safeEndLinePoints[0][0] || 0) * 100;
            const y1 = (safeEndLinePoints[0][1] || 0) * 100;
            const x2 = (safeEndLinePoints[1][0] || 0) * 100;
            const y2 = (safeEndLinePoints[1][1] || 0) * 100;
            
            if (!isNaN(x1) && !isNaN(y1) && !isNaN(x2) && !isNaN(y2)) {
              return <line key="end-line-0" x1={x1} y1={y1} x2={x2} y2={y2} stroke="red" strokeWidth="0.5" />;
            }
          }
          return null;
        })()}

        <path
          className="svg-polygon-path svg-polygon-path-drawing"
          d={polygonPoints.map((p, i) => `${'LM'[+!i]}${getPathStr(p)}`).join(' ')}
        />
      </svg>

      <div className="polygon-container">
        {(shouldRenderAllElements ? areaPoints : (Array.isArray(val) ? val : []))?.map((polygon, polygonIndex) => {
          if (!Array.isArray(polygon)) return null;
          return (
            <React.Fragment key={polygonIndex}>
              {polygon.map((point, pointIndex) => (
                <div
                  key={`${polygonIndex}-${pointIndex}`}
                  style={getStyle(point)}
                  className="polygon-point"
                />
              ))}
            </React.Fragment>
          );
        })}
      </div>

      {/* When showing all, render line endpoints as visible markers */}
      {shouldRenderAllElements && (
        <>
          {safeLinePoints.length > 0 && safeLinePoints.map((line, lineIndex) => (
            <div key={`line-points-${lineIndex}`} className="polygon-container">
              {line.map((point, pointIndex) => (
                <div
                  key={`line-${lineIndex}-${pointIndex}`}
                  style={{ ...getStyle(point), borderColor: 'orange', backgroundColor: 'rgba(255, 165, 0, 0.5)' }}
                  className="polygon-point"
                />
              ))}
            </div>
          ))}

          {safeStartLinePoints.length > 0 && safeStartLinePoints.map((line, lineIndex) => (
            <div key={`start-line-points-${lineIndex}`} className="polygon-container">
              {line.map((point, pointIndex) => (
                <div
                  key={`start-${lineIndex}-${pointIndex}`}
                  style={{ ...getStyle(point), borderColor: 'green', backgroundColor: 'rgba(0, 255, 0, 0.5)' }}
                  className="polygon-point"
                />
              ))}
            </div>
          ))}

          {safeEndLinePoints.length > 0 && safeEndLinePoints.map((line, lineIndex) => (
            <div key={`end-line-points-${lineIndex}`} className="polygon-container">
              {line.map((point, pointIndex) => (
                <div
                  key={`end-${lineIndex}-${pointIndex}`}
                  style={{ ...getStyle(point), borderColor: 'red', backgroundColor: 'rgba(255, 0, 0, 0.5)' }}
                  className="polygon-point"
                />
              ))}
            </div>
          ))}
        </>
      )}

      <div className="polygon-container">
        {polygonPoints.map((point, pointIndex) => (
          <div
            key={`drw-${pointIndex}`}
            className={
              (pointIndex === 0 && isHoveringClosing)
                ? "polygon-point polygon-point-drawing polygon-hovering-closing"
                : "polygon-point polygon-point-drawing"
            }
            style={getStyle(point)}
          />
        ))}
      </div>

      <div className="polygon-container polygon-drawer-click-target"
        ref={polygonDrawerRef}
        onMouseUp={handleClick}
        onMouseMove={handleHover}
      />
    </div>
  )
}

export default PolygonDrawer
